import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { AiOutlineDoubleRight } from 'react-icons/ai';
import { MdOutlineCheckBoxOutlineBlank, MdOutlineCheckBox, MdInfo } from 'react-icons/md';
import Spinner from 'assets/gif/dots.gif';
import axios from 'config/axios';
import Button from 'components/Button';
import Header from 'components/Header';
import Tabs from 'components/Tabs';
import Table from 'components/Table';
import TextInput from 'components/TextInput';

import './index.css';

const ASSIGNMENT_TYPES = ['Preset', 'Custom'];

function Assignments() {
    const [assignments, setAssignments] = useState([]);
    const [assignmentId, setAssignmentId] = useState(null);
    const [students, setStudents] = useState([]);
    const [studentsLoading, setStudentsLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [tabIndex, setTabIndex] = useState(0);

    const navigate = useNavigate();

    const fetchAssignments = () => {
        axios.get('tutors/assignments/')
            .then(res => {
                setAssignments(res.data.questionSets);
            })
            .catch(error => {
                console.log(error);
            });
    }

    const assignAssignment = (studentId: string) => {
        axios.post('tutors/assignments/assign/', {
                studentId: studentId,
                questionSetId: assignmentId
            })
            .then(res => {
                let newStudents = [...students];
                const studentIndex = students.findIndex(student => student.studentId === studentId);
                newStudents[studentIndex] = res.data.student;
                setStudents(newStudents);
            })
            .catch(error => {
                console.log(error);
            });
    }

    useEffect(() => {
        fetchAssignments();
    }, []);

    useEffect(() => {
        if (assignmentId) {
            setStudentsLoading(true);
            setStudents([]);
            axios.get(`tutors/assignments/students/${assignmentId}/`)
                .then(res => {
                    setStudents(res.data.students);
                    setStudentsLoading(false);
                })
                .catch(error => {
                    console.log(error);
                    setStudentsLoading(false);
                });
        }
    }, [assignmentId]);

    useEffect(() => {
        setAssignmentId(null);
    }, [tabIndex]);

    const filteredAssignments = assignments.filter(assignment => assignment.preset
        ? ASSIGNMENT_TYPES[tabIndex] === 'Preset'
        : ASSIGNMENT_TYPES[tabIndex] === 'Custom'
    );

    if (ASSIGNMENT_TYPES[tabIndex] === 'Preset') {
        filteredAssignments.sort((a, b) => (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()));
    }

    return (
        <>
            <Helmet>
                <title>
                    Assignments - Test Center Admin
                </title>
            </Helmet>
            <Header title='Assignments' />
            <div className='assignments'>
                <div className='assignments__assignments'>
                    <div className='assignments__bar1'>
                        <p className='assignments__text'>
                            Select an assignment and assign students
                        </p>
                        <div className='assignments__bar'>
                            <Tabs
                                tabs={ASSIGNMENT_TYPES}
                                tabIndex={tabIndex}
                                onSelect={ASSIGNMENT_TYPES.map((_tab, t) => (
                                    () => setTabIndex(t)
                                ))}
                            />
                            {!assignmentId && (
                                ASSIGNMENT_TYPES[tabIndex] === 'Custom'
                                ?   <Button className='assignments__button' onClick={() => navigate('/assignments/create')}>
                                        Create assignment
                                    </Button>
                                :   <div className='assignments__info'>
                                        <MdInfo className='assignments__infoIcon' />
                                        <p>1 assignment = 1/4 credits</p>
                                    </div>
                            )}
                        </div>
                    </div>
                    <Table
                        key={tabIndex}
                        header={assignmentId
                            ? ['Name']
                            : [
                                'Name',
                                'Test Type',
                                'Subject',
                                '# Questions',
                                ...ASSIGNMENT_TYPES[tabIndex] === 'Custom' ? ['Created At'] : [],
                                'Assign'
                            ]
                        }
                        rows={filteredAssignments.map(assignment => (assignmentId
                            ? [assignment.name]
                            : [
                                assignment.name,
                                assignment.testType,
                                assignment.subject,
                                assignment.numQuestions,
                                ...ASSIGNMENT_TYPES[tabIndex] === 'Custom' ? [new Date(assignment.createdAt).toLocaleDateString()] : [],
                                'Assign'
                            ]
                        ))}
                        columnTypes={assignmentId
                            ? ['string']
                            : [
                                'string',
                                'string',
                                'string',
                                'number',
                                ...ASSIGNMENT_TYPES[tabIndex] === 'Custom' ? ['date'] : [],
                                null
                            ]
                        }
                        onSelectRow={filteredAssignments.map(assignment => (
                            () => setAssignmentId(assignmentId === assignment.id
                                ? null
                                : assignment.id
                            )
                        ))}
                        rowsSelected={filteredAssignments.map(assignment => (
                            assignment.id === assignmentId
                        ))}
                        onSelectCell={filteredAssignments.map(assignment => (assignmentId
                            ? [() => navigate(`/assignments/${assignment.id}`)]
                            : [
                                () => navigate(`/assignments/${assignment.id}`),
                                null,
                                null,
                                null,
                                ...ASSIGNMENT_TYPES[tabIndex] === 'Custom' ? [null] : [],
                                () => setAssignmentId(assignmentId === assignment.id
                                    ? null
                                    : assignment.id
                                )
                            ]
                        ))}
                    />
                </div>
                {assignmentId !== null && <>
                    <AiOutlineDoubleRight className='assignments__arrow' />
                    <div className='assignments__students'>
                        <div className='assignments__bar'>
                            <TextInput
                                className='assignments__search'
                                placeholder='search student'
                                value={search}
                                onChange={event => setSearch(event.target.value)}
                            />
                            <p className='assignments__text'>
                                {students.filter(student => (
                                    student.assigned
                                )).length} {students.filter(student => (
                                    student.assigned
                                )).length > 1 
                                    ? 'students'
                                    : 'student'
                                } assigned
                            </p>
                        </div>
                        <Table
                            header={studentsLoading ? [''] : ['', 'Attempts', 'Name', 'Email']}
                            columnTypes={studentsLoading ? [null] : [null, 'number', 'string', 'string']}
                            rows={studentsLoading ? [[<img className='assignments__loader' src={Spinner} />]] : students.filter(student => (
                                !search ||
                                student.email?.toLowerCase().includes(search.toLowerCase()) ||
                                student.studentId?.toLowerCase().includes(search.toLowerCase()) ||
                                student.firstName?.toLowerCase().includes(search.toLowerCase()) ||
                                student.lastName?.toLowerCase().includes(search.toLowerCase())
                            )).map((student, s) => ([
                                student.assigned
                                    ?   <>
                                            <MdOutlineCheckBox
                                                className={student.inProgress ? 'assignments__checkBox--disabled' : 'assignments__checkBox'}
                                                onClick={() => !student.inProgress && assignAssignment(student.studentId)}
                                            />
                                            {student.inProgress && <img className='assignments__loader' src={Spinner} />}
                                        </>
                                    :   <>
                                            <MdOutlineCheckBoxOutlineBlank
                                                className='assignments__checkBox--blank'
                                                onClick={() => assignAssignment(student.studentId)}
                                            />
                                        </>,
                                student.numCompleted,
                                `${student.firstName} ${student.lastName}`,
                                student.email
                            ]))}
                        />
                    </div>
                </>}
            </div>
        </>
    );
}

export default Assignments;